import {
  DRIVING_RESPAWN_RADIUS,
  DRIVING_SPAWN_HEADING,
  DRIVING_SPAWN_X,
  DRIVING_SPAWN_Z,
} from './stageGround';
import { sampleDrivingTerrain } from './drivingTerrain';
import {
  DRIVING_SURFACE_TYPES,
  resolveDrivingSurface,
  sampleDrivingSurfaceType,
  type DrivingSurfaceType,
} from './drivingSurface';
import {
  DEFAULT_DRIVING_ASSISTS,
  type DrivingAssists,
  type RegenMode,
} from './drivingPreferences';
import {
  DEFAULT_VEHICLE_TUNING,
  resolveVehicleTuning,
  type VehicleTuning,
} from './vehicleTuning';
import { resolveDetailScale, sampleRoadProfile } from './roadProfile';
import type { CourseRoute } from './drivingCourse';
import {
  isCourseObstacleContactActive,
  resolveCourseObstacleCollision,
} from './courseCollision';
import type { CarBounds, ElectricEnergySpec, Powertrain } from '../types';

/**
 * 주행 모드의 차량 운동.
 *
 * 충돌체가 필요한 강체 엔진 대신, 고정 서브스텝으로 계산하는 동적 바이시클 모델을
 * 사용합니다. 엔진 구동력·브레이크·구름 저항·공기 저항·경사 중력과 타이어 그립을
 * 가속도로 합산하고, 그립을 넘는 조향 입력은 언더스티어와 횡방향 슬립으로 바꿉니다.
 *
 * 이 파일은 순수 함수만 둡니다. 렌더는 three/CarDriver가 매 프레임 이 함수를 돌려
 * 결과를 transform에 옮기고, 상태는 React가 아니라 ref에 둡니다(60fps로 setState하면
 * 매 프레임 트리 전체가 다시 렌더됩니다).
 *
 * 좌표 규약은 앱 전체와 같습니다(README): 전면 +X, 위 +Y, 차량 오른쪽 +Z.
 * heading은 +Y 축 회전이라 양수가 좌회전입니다(+X가 −Z 쪽으로 돌아갑니다).
 */

export const STAGE_MODES = {
  /** 기존 동작. 궤도 회전·줌으로 차를 살펴봅니다. */
  VIEWING: 'viewing',
  /** 시점을 고정하고 차를 몰고 다닙니다. */
  DRIVING: 'driving',
} as const;

export type StageMode = (typeof STAGE_MODES)[keyof typeof STAGE_MODES];

/* ---------------------------------------------------------------- 튜닝값 */

const GRAVITY = 9.81;
const MAX_REVERSE_SPEED = 5;
const HUNDRED_KMH_IN_METRES_PER_SECOND = 100 / 3.6;
const REVERSE_ACCELERATION = 6;
const BRAKE_DECELERATION = 11.5;
const BASE_ROLLING_RESISTANCE = 0.14;
const AERO_DRAG_AT_TOP_SPEED_RATIO = 0.18;
const REST_SPEED = 0.02;
const MAX_OVERSPEED_RATIO = 1.2;

/**
 * 전기 구동이 회생으로 회수할 수 있는 출력의 상한. 구동 만눈금 대비 비율입니다.
 *
 * 전기차는 내보내는 출력보다 훨씬 적게만 회수합니다 — 배터리가 받아 줄 수 있는 충전
 * 전력이 한계라서, 세게 밟은 제동의 나머지는 마찰 브레이크가 담당합니다. 실제로도
 * Model 3가 60~70kW대, 회생이 강한 Taycan이 265kW대이므로 만눈금의 1/3 근처가 맞습니다.
 * 차종별 값을 카탈로그에 두지 않고 대표 비율 하나를 쓰는 것은, 이 값이 물리가 아니라
 * 계기 표시에만 쓰이기 때문입니다(감속 자체는 브레이크 모델이 이미 처리합니다).
 */
const REGEN_POWER_LIMIT_RATIO = 0.35;

const REGEN_DECELERATION: Readonly<Record<RegenMode, number>> = Object.freeze({
  standard: 0.6,
  strong: 1.25,
  'one-pedal': 2.1,
});
/** 가속 페달 초반에서 lift-off 회생과 구동 토크를 부드럽게 섞는 범위입니다. */
const REGEN_BLEND_THROTTLE = 0.08;

/**
 * 전기 구동의 단일 감속기 기어비.
 *
 * 전기차는 변속기가 없지만 모터는 돌기 때문에 회전수 자체는 존재합니다. 양산 전기차의
 * 1단 감속기는 대체로 8~9.7:1 구간에 있어서(예: Taycan 앞축 8.05:1) 대표값 하나를 씁니다.
 * 차종별 기어비를 카탈로그에 넣지 않는 이유는, 전기차 계기판이 회전수를 보여 주지
 * 않아서 이 값이 표시에 쓰이지 않기 때문입니다 — 상태를 비워 두지 않으려는 용도입니다.
 */
const ELECTRIC_REDUCTION_RATIO = 9;

/** 정지 상태의 앞바퀴 최대 조향각 (rad, 약 41°). */
const MAX_STEER_ANGLE = 0.72;
/**
 * 운전자 보조가 허용하는 최대 요청 횡가속도입니다.
 *
 * 실제 타이어 한계보다 높게 두어 고속에서 핸들을 끝까지 꺾으면 슬립이 생기되,
 * 키보드 입력 하나로 조향각 40°가 즉시 들어가 차가 팽이처럼 돌지는 않게 합니다.
 */
const MAX_REQUESTED_LATERAL_ACCEL = 14;
/** 마른 노면의 타이어 한계. 약 0.92g입니다. */
const DRY_TIRE_GRIP_ACCEL = GRAVITY * 0.92;
const STEER_RATE = 3.2;
const STEER_RETURN_RATE = 5;
const YAW_RATE_RESPONSE = 3.6;
const SLIP_RECOVERY_RATE = 4.8;
const SLIP_LATERAL_ACCEL_SCALE = 0.72;
const MAX_LATERAL_SPEED_RATIO = 0.38;
const SLIP_MIN_SPEED = 12;
const SLIP_FULL_SPEED = 28;
const SLIP_RISE_RATE = 5.5;
const SLIP_FALL_RATE = 2.2;

/* --- 차체 기울기: 운동에 되먹임되지 않는 시각 효과입니다 --- */

const MAX_BODY_TILT = 0.03;
const LATERAL_ACCEL_FOR_MAX_ROLL = DRY_TIRE_GRIP_ACCEL;
const LONGITUDINAL_ACCEL_FOR_MAX_PITCH = 8;
const BODY_TILT_RATE = 6;

/** 탭 복귀 때 긴 시간을 버리고, 남은 시간은 안정적인 고정 간격으로 나눕니다. */
const MAX_FRAME_STEP_SECONDS = 1 / 20;
const PHYSICS_STEP_SECONDS = 1 / 120;

/* ---------------------------------------------------------------- 타입 */

export const SPAWN_REASONS = {
  INITIAL: 'initial',
  RUNOFF: 'runoff',
  RECENTER: 'recenter',
  COURSE_CHANGE: 'course-change',
} as const;

export type SpawnReason = (typeof SPAWN_REASONS)[keyof typeof SPAWN_REASONS];

export interface VehicleSpawn {
  x: number;
  z: number;
  heading: number;
}

/** 바퀴 네 곳. 앞/뒤 × 좌/우입니다. */
export type WheelCorner = 'frontLeft' | 'frontRight' | 'rearLeft' | 'rearRight';

export type WheelTravel = Record<WheelCorner, number>;

export const WHEEL_CORNERS: readonly WheelCorner[] = [
  'frontLeft',
  'frontRight',
  'rearLeft',
  'rearRight',
];

function createWheelTravel(): WheelTravel {
  return { frontLeft: 0, frontRight: 0, rearLeft: 0, rearRight: 0 };
}

const DEFAULT_VEHICLE_SPAWN: Readonly<VehicleSpawn> = Object.freeze({
  x: DRIVING_SPAWN_X,
  z: DRIVING_SPAWN_Z,
  heading: DRIVING_SPAWN_HEADING,
});

export interface ElectricEnergyState {
  usableBatteryKWh: number;
  referenceConsumptionWhPerKm: number;
  remainingKWh: number;
  consumedKWh: number;
  recoveredKWh: number;
  distanceKm: number;
  /** 배터리 기준 순간전력. 양수는 방전, 음수는 충전입니다. */
  batteryPowerKw: number;
}

/** 실제 오브젝트 충돌의 감쇠 레벨과 마지막 일회성 이벤트입니다. */
export interface VehicleCollisionState {
  /** HUD 등 연속 효과가 읽는 0~1 감쇠 레벨입니다. */
  impact: number;
  /** 새 충돌마다 증가해 카메라·햅틱이 이벤트를 한 번씩만 소비하게 합니다. */
  sequence: number;
  /** 마지막 이벤트의 원래 충격 강도입니다. 폴링 사이에도 사라지지 않습니다. */
  strength: number;
  /** 장애물에서 차량을 향하는 마지막 충돌의 월드 평면 법선입니다. */
  normalX: number;
  normalZ: number;
  obstacleId: string | null;
}

function createVehicleCollisionState(sequence = 0): VehicleCollisionState {
  return {
    impact: 0,
    sequence,
    strength: 0,
    normalX: 0,
    normalZ: 0,
    obstacleId: null,
  };
}

/** 차량 운동 상태. 전부 앱 좌표계·SI 단위입니다. */
export interface VehicleState {
  x: number;
  z: number;
  /** yaw (rad). 0이면 전면이 +X를 봅니다. 양수가 좌회전. */
  heading: number;
  /** 노면을 따라 측정한 전후 속도(m/s). 음수는 후진입니다. */
  speed: number;
  /**
   * 변속 표시. 후진/중립은 문자, 전진은 1~6단입니다.
   *
   * 전기 구동은 변속할 단이 없으므로 전진에서 'D'만 씁니다.
   */
  gear: 'R' | 'N' | 'D' | 1 | 2 | 3 | 4 | 5 | 6;
  /**
   * 구동 회전수.
   *
   * 내연기관·PHEV는 가상 엔진 회전수(아이들 800rpm 포함)이고, 전기 구동은 감속기를
   * 거꾸로 올린 실제 모터 회전수입니다. 전기차는 정지하면 0입니다 — 아이들링이 없습니다.
   * 전기차 계기판은 이 값을 보여 주지 않고 powerKw를 씁니다.
   */
  rpm: number;
  /**
   * 구동계가 주고받는 순간 출력 (kW). 음수는 회수(전기 구동의 회생)입니다.
   *
   * 질량 × 접지된 구동가속도 × 속도입니다. 구동가속도에는 구름·공기 저항 보상이 이미
   * 들어 있어서, 최고속도로 정속 주행하면 0이 아니라 저항과 균형을 이루는 값이 나옵니다.
   *
   * 전기 구동의 음수는 회생 한계에서 멈춥니다 — 그 아래 감속분은 모터가 아니라 마찰
   * 브레이크가 만들기 때문입니다. 계기판은 전기 구동에서만 이 값을 보여 줍니다.
   */
  powerKw: number;
  /** 순수 전기차의 세션 에너지. ICE/PHEV는 null입니다. */
  energy: ElectricEnergyState | null;
  /** 진행 방향 기준 노면 경사(%). 양수는 오르막입니다. */
  gradePercent: number;
  /** 이번 서브스텝의 실제 종가속도(m/s²). */
  longitudinalAcceleration: number;
  /** 현재 차량 중심이 밟고 있는 지역 노면과 합성 물리값입니다. */
  surfaceType: DrivingSurfaceType;
  surfaceWetness: number;
  effectiveGrip: number;
  /** 각 보조 장치가 현재 개입 중인지 HUD에 전달합니다. */
  absActive: boolean;
  tcsActive: boolean;
  escActive: boolean;
  /** 차량 오른쪽(+Z) 방향 횡속도(m/s). 실제 미끄러짐을 월드 이동에 반영합니다. */
  lateralSpeed: number;
  /** 실제 yaw 변화율(rad/s). 요청 조향을 타이어가 감당할 수 있는 속도로 따라갑니다. */
  yawRate: number;
  /** 현재 앞바퀴 조향각 (rad). 입력을 향해 서서히 따라갑니다. */
  steerAngle: number;
  /** 속도 벡터와 차체 전면 사이의 각도(rad). 양수면 오른쪽으로 흐릅니다. */
  slipAngle: number;
  /** 구동·제동 요청이 남은 타이어 그립을 넘은 비율. */
  longitudinalSlip: number;
  /** 이번 서브스텝의 즉시 슬립 강도. 흔적 생성 여부를 판정할 때 씁니다. */
  instantaneousSlip: number;
  /** 효과/HUD가 쓰는 0~1 평활 슬립 강도. 이미 생긴 효과의 세기를 부드럽게 합니다. */
  slipIntensity: number;
  /**
   * 가감속·코너링이 만드는 차체 롤·피치 (rad).
   *
   * 서스펜션 성분과 **더해서 쓰지 않고 따로 둡니다**. 한 필드에 합치면 다음 서브스텝의
   * approach가 이미 더해진 값을 기준으로 다시 더해서 기울기가 발산합니다.
   */
  roll: number;
  pitch: number;
  /** 요철이 만드는 롤·피치 (rad). 렌더에서 위 값과 더해 적용합니다. */
  suspensionRoll: number;
  suspensionPitch: number;
  /** 바퀴별 서스펜션 스트로크(m). 양수는 차체가 올라간 상태입니다. */
  wheelTravel: WheelTravel;
  /** 바퀴별 스트로크 속도(m/s). 감쇠비를 표현하려면 속도 상태가 필요합니다. */
  wheelVelocity: WheelTravel;
  /** 차체 상하 변위(m)와 속도(m/s). 네 바퀴 평균에서 나옵니다. */
  bodyHeave: number;
  bodyHeaveVelocity: number;
  /** 최근 노면 충격 세기(0~1). 진동·HUD가 읽습니다. */
  roadImpact: number;
  /** 콘 등 실제 코스 오브젝트와의 충돌 상태입니다. */
  collision: VehicleCollisionState;
  /** 바퀴 피벗의 Z축에 적용할 누적 회전각(rad). */
  wheelSpin: number;
  /** 출발선에 놓인 횟수. 카메라가 순간이동을 감지하는 버전 값입니다. */
  spawnCount: number;
  /** 마지막으로 출발선에 놓인 이유. */
  spawnReason: SpawnReason;
}

export interface VehicleInput {
  /** −1(후진) ~ 1(전진). */
  throttle: number;
  /** −1(우) ~ 1(좌). heading과 부호를 맞춰 둡니다. */
  steer: number;
  /**
   * 0~1 제동 입력.
   *
   * 게임패드 트리거를 아날로그로 받기 위해 수치입니다. 키보드·터치는 0 또는 1만
   * 보냅니다. 판정은 BRAKE_INPUT_THRESHOLD와 비교합니다 — 트리거는 놓은 상태에서도
   * 미세한 값을 보내는 기기가 있어서 0과 정확히 비교하면 브레이크가 계속 걸립니다.
   */
  brake: number;
}

/** 이 값을 넘으면 제동 입력으로 봅니다. */
export const BRAKE_INPUT_THRESHOLD = 0.05;

export function isBraking(input: VehicleInput): boolean {
  return input.brake > BRAKE_INPUT_THRESHOLD;
}

export interface VehicleSpec {
  /** 구동 방식. 변속 표시와 계기판 종류를 정합니다. */
  powertrain: Powertrain;
  /** 공차중량(kg). 출력(kW) 계산에만 씁니다 — 운동은 가속도로 적분합니다. */
  massKg: number;
  /**
   * 출력 게이지의 만눈금 (kW).
   *
   * 카탈로그 0→100 가속도를 100km/h(또는 최고속도의 92%)에서 낸 출력입니다. 제조사
   * 공개 출력과 정확히 같지는 않지만(구동계 손실을 모델에 두지 않았습니다) 같은 자리를
   * 가리키므로 게이지 기준으로는 맞습니다.
   */
  peakPowerKw: number;
  /** 회생으로 회수할 수 있는 출력 상한 (kW). 전기 구동이 아니면 0입니다. */
  regenLimitKw: number;
  /** 순수 전기차의 배터리/효율 제원. ICE/PHEV는 null입니다. */
  electricEnergy: ElectricEnergySpec | null;
  wheelbase: number;
  tireOuterRadius: number;
  maxForwardSpeed: number;
  forwardAcceleration: number;
  /** 튜닝이 적용된 최대 제동 감속도(m/s²). */
  brakeDeceleration: number;
  /**
   * ABS가 제동 요청을 잘라 내는 종방향 접지 비율.
   *
   * 스포츠 브레이크는 패드·냉각·바이어스가 좋아 타이어 한계에 더 가깝게 쓸 수 있습니다.
   * 제동력 자체는 이미 타이어 한계에 막혀 있어서, 이 값이 실제 제동거리를 바꿉니다.
   */
  absLimitRatio: number;
  /** 타이어 튜닝의 그립 계수. 노면·날씨 계수와 곱해집니다. */
  tireGripMultiplier: number;
  /** 타이어 튜닝의 구름 저항 계수. */
  tireRollingResistanceMultiplier: number;
  /** 서스펜션 강성(1/s²), 감쇠(1/s), 최대 스트로크(m)와 요철 반응 계수입니다. */
  suspensionStiffness: number;
  suspensionDamping: number;
  suspensionTravel: number;
  bumpResponse: number;
  /** 바퀴 접지점 계산용 실측 치수입니다. */
  frontWheelX: number;
  rearWheelX: number;
  wheelHalfTrack: number;
}

/** 날씨/노면이 물리 계산에 넘기는 최소 표면 정보입니다. */
export interface VehicleSurfaceCondition {
  /** 1은 마른 노면. 비·눈에서 낮아집니다. */
  gripMultiplier: number;
  /** 1은 기본 아스팔트 구름 저항입니다. */
  rollingResistanceMultiplier: number;
  /** 전역 날씨의 젖음. 지역 물웅덩이와 큰 값을 사용합니다. */
  roadWetness?: number;
}

export const DEFAULT_VEHICLE_SURFACE: Readonly<VehicleSurfaceCondition> = Object.freeze({
  gripMultiplier: 1,
  rollingResistanceMultiplier: 1,
});

export const NEUTRAL_INPUT: VehicleInput = { throttle: 0, steer: 0, brake: 0 };

export function createVehicleState(
  spawn: VehicleSpawn = DEFAULT_VEHICLE_SPAWN
): VehicleState {
  return {
    x: spawn.x,
    z: spawn.z,
    heading: spawn.heading,
    speed: 0,
    gear: 'N',
    // 첫 서브스텝이 파워트레인에 맞는 값으로 덮어씁니다. 여기서 아이들 회전수를 넣으면
    // 전기차도 한 프레임 동안 회전수를 갖게 되므로 정지값인 0으로 둡니다.
    rpm: 0,
    powerKw: 0,
    energy: null,
    gradePercent: 0,
    longitudinalAcceleration: 0,
    surfaceType: DRIVING_SURFACE_TYPES.ASPHALT,
    surfaceWetness: 0,
    effectiveGrip: 1,
    absActive: false,
    tcsActive: false,
    escActive: false,
    lateralSpeed: 0,
    yawRate: 0,
    steerAngle: 0,
    slipAngle: 0,
    longitudinalSlip: 0,
    instantaneousSlip: 0,
    slipIntensity: 0,
    roll: 0,
    pitch: 0,
    suspensionRoll: 0,
    suspensionPitch: 0,
    wheelTravel: createWheelTravel(),
    wheelVelocity: createWheelTravel(),
    bodyHeave: 0,
    bodyHeaveVelocity: 0,
    roadImpact: 0,
    collision: createVehicleCollisionState(),
    wheelSpin: 0,
    spawnCount: 0,
    spawnReason: SPAWN_REASONS.INITIAL,
  };
}

export function respawnVehicle(
  state: VehicleState,
  reason: SpawnReason,
  spawn: VehicleSpawn = DEFAULT_VEHICLE_SPAWN
): VehicleState {
  return {
    ...createVehicleState(spawn),
    // 이탈·재센터·코스 변경은 차의 위치만 바꿉니다. 배터리가 갑자기 충전되거나
    // 주행 전비가 사라지면 순간이동이 에너지 생성 장치가 되므로 세션 에너지는 보존합니다.
    energy: state.energy === null
      ? null
      : { ...state.energy, batteryPowerKw: 0 },
    // 카메라·햅틱의 소비 cursor보다 sequence가 작아지지 않게 버전은 보존합니다.
    collision: createVehicleCollisionState(state.collision.sequence),
    spawnCount: state.spawnCount + 1,
    spawnReason: reason,
  };
}

/**
 * 주행 제원을 만들 때 카탈로그에서 가져오는 값들.
 *
 * 위치 인자로 늘어놓지 않는 이유: topSpeedKmh·zeroTo100KmhSeconds·kerbWeightKg가 모두
 * 숫자여서, 순서가 하나 밀려도 타입 검사를 통과한 채 조용히 다른 차가 됩니다.
 */
export interface VehicleProfile {
  bounds: CarBounds;
  powertrain: Powertrain;
  topSpeedKmh: number;
  zeroTo100KmhSeconds: number;
  kerbWeightKg: number;
  electricEnergy?: ElectricEnergySpec;
}

function peakPowerKwOf(
  massKg: number,
  forwardAcceleration: number,
  maxForwardSpeed: number
): number {
  const referenceSpeed = Math.min(HUNDRED_KMH_IN_METRES_PER_SECOND, maxForwardSpeed * 0.92);
  return (massKg * forwardAcceleration * referenceSpeed) / 1000;
}

/**
 * 카탈로그 값만으로 낸 출력 게이지 만눈금 (kW).
 *
 * 계기판이 기본 셋업 기준으로 고정된 눈금을 쓰도록 튜닝을 넣지 않습니다. 튜닝마다
 * 눈금이 달라지면 같은 바 길이가 다른 출력을 뜻하게 되어 비교가 되지 않습니다.
 */
export function catalogPeakPowerKw(profile: VehicleProfile): number {
  return createVehicleSpec(profile).peakPowerKw;
}

/**
 * 제원과 튜닝에서 주행 제원을 만듭니다.
 *
 * 카탈로그의 최고속도·0→100 값은 **기본 셋업의 기준선**입니다. 튜닝은 그 값을 덮어쓰지
 * 않고 계수로만 조정하므로, 기본값으로 되돌리면 제조사 수치가 그대로 복원됩니다.
 */
export function createVehicleSpec(
  profile: VehicleProfile,
  tuning: VehicleTuning = DEFAULT_VEHICLE_TUNING
): VehicleSpec {
  const {
    bounds,
    powertrain,
    topSpeedKmh,
    zeroTo100KmhSeconds,
    kerbWeightKg,
    electricEnergy: catalogElectricEnergy,
  } = profile;
  const resolved = resolveVehicleTuning(tuning);
  const maxForwardSpeed = (topSpeedKmh / 3.6) * resolved.topSpeedMultiplier;
  const forwardAcceleration =
    (HUNDRED_KMH_IN_METRES_PER_SECOND / zeroTo100KmhSeconds) * resolved.accelerationMultiplier;
  const peakPowerKw = peakPowerKwOf(kerbWeightKg, forwardAcceleration, maxForwardSpeed);
  /*
   * 테스트용/외부 호출자가 EV 제원을 생략해도 운동은 작동하게 보수적 fallback을 둡니다.
   * 실제 카탈로그의 모든 EV는 고유 제원을 명시합니다.
   */
  const electricEnergy: ElectricEnergySpec | null = powertrain === 'ev'
    ? catalogElectricEnergy ?? {
        usableBatteryKWh: 75,
        initialSoc: 0.8,
        regenLimitKw: peakPowerKw * REGEN_POWER_LIMIT_RATIO,
        driveEfficiency: 0.9,
        regenEfficiency: 0.75,
        referenceConsumptionWhPerKm: 180,
        accessoryPowerKw: 0.7,
      }
    : null;
  return {
    powertrain,
    massKg: kerbWeightKg,
    peakPowerKw,
    regenLimitKw: electricEnergy?.regenLimitKw ?? 0,
    electricEnergy,
    wheelbase: bounds.frontWheelX - bounds.rearWheelX,
    tireOuterRadius: bounds.tireOuterRadius,
    maxForwardSpeed,
    forwardAcceleration,
    brakeDeceleration: BRAKE_DECELERATION * resolved.brakeMultiplier,
    absLimitRatio: resolved.absLimitRatio,
    tireGripMultiplier: resolved.gripMultiplier,
    tireRollingResistanceMultiplier: resolved.rollingResistanceMultiplier,
    suspensionStiffness: resolved.suspensionStiffness,
    suspensionDamping: resolved.suspensionDamping,
    suspensionTravel: resolved.suspensionTravel,
    bumpResponse: resolved.bumpResponse,
    frontWheelX: bounds.frontWheelX,
    rearWheelX: bounds.rearWheelX,
    wheelHalfTrack: bounds.wheelHalfTrack,
  };
}

export function createElectricEnergyState(spec: ElectricEnergySpec): ElectricEnergyState {
  return {
    usableBatteryKWh: spec.usableBatteryKWh,
    referenceConsumptionWhPerKm: spec.referenceConsumptionWhPerKm,
    remainingKWh: spec.usableBatteryKWh * clamp01(spec.initialSoc),
    consumedKWh: 0,
    recoveredKWh: 0,
    distanceKm: 0,
    batteryPowerKw: 0,
  };
}

/** 세션 누적 순소비 전비. 100m 전에는 표본이 너무 작아 null입니다. */
export function getTripConsumptionWhPerKm(energy: ElectricEnergyState): number | null {
  if (energy.distanceKm < 0.1) {
    return null;
  }
  return ((energy.consumedKWh - energy.recoveredKWh) * 1000) / energy.distanceKm;
}

/**
 * 남은 에너지로 갈 수 있는 예상 거리.
 * 2km까지는 카탈로그 복합 전비와 실제 누적 전비를 섞어 출발 직후 값이 튀지 않게 합니다.
 */
export function getEstimatedRangeKm(energy: ElectricEnergyState): number {
  const trip = getTripConsumptionWhPerKm(energy);
  const blend = trip === null ? 0 : clamp01(energy.distanceKm / 2);
  // 긴 내리막에서 순소비가 음수가 되어도 무한 주행거리로 표시하지 않습니다.
  const measured = trip === null ? energy.referenceConsumptionWhPerKm : Math.max(80, trip);
  const forecast =
    energy.referenceConsumptionWhPerKm * (1 - blend) + measured * blend;
  return (energy.remainingKWh * 1000) / Math.max(1, forecast);
}

interface ElectricTractionLimit {
  acceleration: number;
  limited: boolean;
}

/**
 * 남은 배터리가 이번 서브스텝에 공급할 수 있는 만큼만 구동 가속도를 허용합니다.
 * 감속/회생은 제한하지 않으므로 0kWh에서도 굴러가고 제동해 에너지를 다시 얻을 수 있습니다.
 */
function limitElectricTraction(
  state: VehicleState,
  spec: VehicleSpec,
  acceleration: number,
  input: VehicleInput,
  dt: number
): ElectricTractionLimit {
  const electric = spec.electricEnergy;
  if (electric === null) {
    return { acceleration, limited: false };
  }
  const travelDirection =
    Math.abs(state.speed) >= REST_SPEED ? Math.sign(state.speed) : Math.sign(input.throttle);
  if (travelDirection === 0 || acceleration * travelDirection <= 0) {
    return { acceleration, limited: false };
  }

  const energy =
    state.energy !== null
    && Math.abs(state.energy.usableBatteryKWh - electric.usableBatteryKWh) < 1e-6
      ? state.energy
      : createElectricEnergyState(electric);
  const accessoryKWh = (electric.accessoryPowerKw * dt) / 3600;
  const availableForTractionKWh = Math.max(0, energy.remainingKWh - accessoryKWh);
  const predictedSpeed = state.speed + acceleration * dt;
  const averageSpeed = (Math.abs(state.speed) + Math.abs(predictedSpeed)) * 0.5;
  const mechanicalKw = (spec.massKg * Math.abs(acceleration) * averageSpeed) / 1000;
  const requestedBatteryKWh =
    (mechanicalKw / Math.max(0.01, electric.driveEfficiency) * dt) / 3600;
  if (requestedBatteryKWh <= availableForTractionKWh || requestedBatteryKWh <= 1e-12) {
    return { acceleration, limited: false };
  }
  const scale = clamp01(availableForTractionKWh / requestedBatteryKWh);
  return { acceleration: acceleration * scale, limited: true };
}

function stepElectricEnergy(
  previous: ElectricEnergyState | null,
  spec: ElectricEnergySpec | null,
  wheelPowerKw: number,
  averageSpeed: number,
  averageLateralSpeed: number,
  dt: number
): ElectricEnergyState | null {
  if (spec === null) {
    return null;
  }
  const energy =
    previous !== null && Math.abs(previous.usableBatteryKWh - spec.usableBatteryKWh) < 1e-6
      ? previous
      : createElectricEnergyState(spec);

  const tractionMechanicalKw = Math.max(0, wheelPowerKw);
  const brakingMechanicalKw = Math.max(0, -wheelPowerKw);
  const acceptedRegenMechanicalKw = Math.min(brakingMechanicalKw, spec.regenLimitKw);
  const requestedBatteryDrawKw = tractionMechanicalKw / spec.driveEfficiency + spec.accessoryPowerKw;
  const requestedBatteryChargeKw = acceptedRegenMechanicalKw * spec.regenEfficiency;

  const consumedKWh = Math.min(energy.remainingKWh, (requestedBatteryDrawKw * dt) / 3600);
  const afterConsumption = energy.remainingKWh - consumedKWh;
  const recoveredKWh = Math.min(
    spec.usableBatteryKWh - afterConsumption,
    (requestedBatteryChargeKw * dt) / 3600
  );
  const distanceKm =
    (Math.hypot(averageSpeed, averageLateralSpeed) * dt) / 1000;

  return {
    ...energy,
    remainingKWh: afterConsumption + recoveredKWh,
    consumedKWh: energy.consumedKWh + consumedKWh,
    recoveredKWh: energy.recoveredKWh + recoveredKWh,
    distanceKm: energy.distanceKm + distanceKm,
    batteryPowerKw: dt <= 0 ? 0 : ((consumedKWh - recoveredKWh) * 3600) / dt,
  };
}

/* ---------------------------------------------------------------- 적분 */

function clamp(value: number, limit: number): number {
  return Math.min(limit, Math.max(-limit, value));
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** 목표를 향해 rate(단위/초)로 다가갑니다. 넘어서지 않습니다. */
function approach(current: number, goal: number, rate: number, dt: number): number {
  const delta = goal - current;
  const step = rate * dt;
  if (Math.abs(delta) <= step) {
    return goal;
  }
  return current + Math.sign(delta) * step;
}

/** 각도를 작게 감아 장시간 주행 뒤에도 float 정밀도를 유지합니다. */
function wrapAngle(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

/**
 * 조작 가능한 속도별 최대 조향각.
 *
 * 이것은 타이어 한계가 아니라 입력 보조입니다. 요청 횡가속도를 실제 그립보다 약 55%
 * 높게 허용하므로 한계를 넘는 부분은 아래 적분기에서 언더스티어와 슬립으로 나타납니다.
 */
function assistedMaxSteerAngle(speed: number, wheelbase: number): number {
  const absSpeed = Math.abs(speed);
  if (wheelbase <= 0 || absSpeed < REST_SPEED) {
    return MAX_STEER_ANGLE;
  }
  const assistedLimit = Math.atan(
    (MAX_REQUESTED_LATERAL_ACCEL * wheelbase) / (absSpeed * absSpeed)
  );
  return Math.min(MAX_STEER_ANGLE, assistedLimit);
}

function stepSteerAngle(
  steerAngle: number,
  input: VehicleInput,
  speed: number,
  dt: number,
  spec: VehicleSpec
): number {
  const maxAngle = assistedMaxSteerAngle(speed, spec.wheelbase);
  const goal = clamp(input.steer, 1) * maxAngle;
  const rate = input.steer === 0 ? STEER_RETURN_RATE : STEER_RATE;
  return clamp(approach(steerAngle, goal, rate, dt), maxAngle);
}

function smoothstep01(value: number): number {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
}

function rollingResistanceFor(surface: VehicleSurfaceCondition): number {
  return BASE_ROLLING_RESISTANCE * Math.max(0.25, surface.rollingResistanceMultiplier);
}

function aerodynamicDragMagnitude(speed: number, spec: VehicleSpec): number {
  const speedRatio = Math.abs(speed) / Math.max(spec.maxForwardSpeed, REST_SPEED);
  const dragAtTopSpeed = Math.max(
    0.25,
    spec.forwardAcceleration * AERO_DRAG_AT_TOP_SPEED_RATIO
  );
  return dragAtTopSpeed * speedRatio * speedRatio;
}

function blendElectricLiftOff(
  speed: number,
  throttleMagnitude: number,
  pedalRequest: number,
  spec: VehicleSpec,
  regenMode: RegenMode
): number {
  if (
    spec.powertrain !== 'ev'
    || Math.abs(speed) < REST_SPEED
    || throttleMagnitude >= REGEN_BLEND_THROTTLE
  ) {
    return pedalRequest;
  }
  const liftOffRequest = -Math.sign(speed) * REGEN_DECELERATION[regenMode];
  const blend = smoothstep01(throttleMagnitude / REGEN_BLEND_THROTTLE);
  return liftOffRequest + (pedalRequest - liftOffRequest) * blend;
}

function requestedTireAcceleration(
  speed: number,
  input: VehicleInput,
  spec: VehicleSpec,
  surface: VehicleSurfaceCondition,
  regenMode: RegenMode
): number {
  if (isBraking(input)) {
    // 아날로그 트리거는 밟은 만큼만 제동력을 냅니다. 총 제동력은 기존 계약 그대로이며,
    // 전기차에서는 배터리가 받을 수 있는 몫만 아래 에너지 적분에서 회생으로 분류합니다.
    const brakeForce = spec.brakeDeceleration * clamp01(input.brake);
    return Math.abs(speed) < REST_SPEED ? 0 : -Math.sign(speed) * brakeForce;
  }

  const throttle = clamp(input.throttle, 1);
  if (throttle === 0) {
    return spec.powertrain === 'ev' && Math.abs(speed) >= REST_SPEED
      ? -Math.sign(speed) * REGEN_DECELERATION[regenMode]
      : 0;
  }

  // 진행 방향 반대 페달은 기어가 바뀌기 전에 먼저 제동합니다(튜닝된 제동력을 씁니다).
  if (speed * throttle < -REST_SPEED) {
    const pedalRequest = Math.sign(throttle) * spec.brakeDeceleration * Math.abs(throttle);
    return blendElectricLiftOff(speed, Math.abs(throttle), pedalRequest, spec, regenMode);
  }

  if (throttle > 0) {
    const forwardSpeed = Math.max(0, speed);
    const calibrationSpeed = Math.min(
      HUNDRED_KMH_IN_METRES_PER_SECOND,
      spec.maxForwardSpeed * 0.92
    );
    const rollingResistance = rollingResistanceFor(surface);
    const calibratedForce =
      spec.forwardAcceleration + rollingResistance + aerodynamicDragMagnitude(forwardSpeed, spec);
    let pedalRequest: number;
    if (forwardSpeed <= calibrationSpeed) {
      // 카탈로그의 0→100 값은 순가속도 계약입니다. 구름/공기 저항을 먼저 보상해야
      // 적분 뒤 실제 평균 가속도가 제조사 값과 일치합니다.
      pedalRequest = calibratedForce * throttle;
    } else {
      const progress = smoothstep01(
        (forwardSpeed - calibrationSpeed) /
          Math.max(REST_SPEED, spec.maxForwardSpeed - calibrationSpeed)
      );
      const forceAtCalibration =
        spec.forwardAcceleration +
        rollingResistance +
        aerodynamicDragMagnitude(calibrationSpeed, spec);
      const forceAtTopSpeed =
        rollingResistance + aerodynamicDragMagnitude(spec.maxForwardSpeed, spec);
      pedalRequest =
        (forceAtCalibration + (forceAtTopSpeed - forceAtCalibration) * progress) * throttle;
    }
    return blendElectricLiftOff(speed, throttle, pedalRequest, spec, regenMode);
  }

  const reverseRatio = clamp01(Math.max(0, -speed) / MAX_REVERSE_SPEED);
  const forceTaper = 1 - 0.88 * reverseRatio * reverseRatio;
  const pedalRequest = -REVERSE_ACCELERATION * Math.abs(throttle) * forceTaper;
  return blendElectricLiftOff(speed, Math.abs(throttle), pedalRequest, spec, regenMode);
}

interface LongitudinalStep {
  speed: number;
  acceleration: number;
  longitudinalSlip: number;
}

function stepLongitudinalSpeed(
  speed: number,
  input: VehicleInput,
  dt: number,
  spec: VehicleSpec,
  gradeAcceleration: number,
  requestedTireAccelerationValue: number,
  supportedTireAcceleration: number,
  longitudinalGrip: number,
  surface: VehicleSurfaceCondition,
  regenMode: RegenMode
): LongitudinalStep {
  /*
   * 원페달은 bounded 회생 감속이 실제로 0을 통과한 뒤에만 정지 hold를 겁니다.
   * 0.25m/s 같은 넓은 구간을 즉시 0으로 만들면 한 서브스텝에 수십 m/s²가 생깁니다.
   */
  if (
    spec.powertrain === 'ev'
    && regenMode === 'one-pedal'
    && !isBraking(input)
    && input.throttle === 0
    && speed === 0
  ) {
    return { speed: 0, acceleration: 0, longitudinalSlip: 0 };
  }

  /*
   * 브레이크는 정지 상태에서 경사 중력을 상쇄하는 홀드 역할도 합니다. 아날로그 입력을
   * 살짝만 밟은 경우에는 완전히 붙잡지 않아야 트리거 감각이 살아납니다.
   */
  if (isBraking(input) && Math.abs(speed) < 0.12 && input.brake >= 0.5) {
    return { speed: 0, acceleration: -speed / dt, longitudinalSlip: 0 };
  }

  const longitudinalSlip =
    longitudinalGrip <= REST_SPEED
      ? requestedTireAccelerationValue === 0
        ? 0
        : Math.sign(requestedTireAccelerationValue)
      : (requestedTireAccelerationValue - supportedTireAcceleration) / longitudinalGrip;
  const rollingResistance = rollingResistanceFor(surface);
  const aerodynamicDrag =
    Math.abs(speed) < REST_SPEED
      ? 0
      : -Math.sign(speed) * aerodynamicDragMagnitude(speed, spec);

  const beforeRolling = supportedTireAcceleration + gradeAcceleration + aerodynamicDrag;
  let acceleration: number;
  if (Math.abs(speed) < REST_SPEED) {
    // 정지 마찰보다 작은 힘은 차를 미세하게 떨게 하지 않습니다. 가파른 내리막의 중력은
    // 이 값을 넘으므로 페달을 놓은 차가 자연스럽게 아래로 굴러갑니다.
    acceleration =
      Math.abs(beforeRolling) <= rollingResistance
        ? 0
        : beforeRolling - Math.sign(beforeRolling) * rollingResistance;
  } else {
    acceleration = beforeRolling - Math.sign(speed) * rollingResistance;
  }

  let nextSpeed = speed + acceleration * dt;
  const isActivelyStopping =
    isBraking(input) || (input.throttle !== 0 && speed * input.throttle < -REST_SPEED);
  if (isActivelyStopping && speed !== 0 && Math.sign(nextSpeed) !== Math.sign(speed)) {
    nextSpeed = 0;
  } else if (input.throttle === 0 && speed !== 0 && Math.sign(nextSpeed) !== Math.sign(speed)) {
    nextSpeed = 0;
  }

  nextSpeed = Math.min(
    spec.maxForwardSpeed * MAX_OVERSPEED_RATIO,
    Math.max(-MAX_REVERSE_SPEED * MAX_OVERSPEED_RATIO, nextSpeed)
  );

  return {
    speed: Math.abs(nextSpeed) < REST_SPEED ? 0 : nextSpeed,
    acceleration: (nextSpeed - speed) / dt,
    longitudinalSlip,
  };
}

/**
 * 바퀴 속도에서 모터 회전수를 올립니다.
 *
 * 전기차에 "회전수가 없다"는 흔한 표현은 정확히는 다단 변속기와 아이들링이 없다는
 * 뜻입니다. 모터는 돌고, 감속기 기어비만큼 바퀴보다 훨씬 빠릅니다.
 */
function electricMotorRpm(speed: number, tireOuterRadius: number): number {
  if (tireOuterRadius <= 0) {
    return 0;
  }
  const wheelRevolutionsPerMinute = (Math.abs(speed) / (2 * Math.PI * tireOuterRadius)) * 60;
  return Math.round(wheelRevolutionsPerMinute * ELECTRIC_REDUCTION_RATIO);
}

/**
 * 속도에서 변속 표시와 회전수를 만듭니다.
 *
 * 전기 구동은 단수가 없어 전진이 항상 'D'이고, 정지하면 회전수도 0입니다. 내연기관과
 * PHEV는 6단 자동을 흉내내고 정지 상태에서 아이들 회전수를 유지합니다.
 */
function deriveGearAndRpm(speed: number, spec: VehicleSpec): Pick<VehicleState, 'gear' | 'rpm'> {
  const electric = spec.powertrain === 'ev';

  if (speed < -REST_SPEED) {
    if (electric) {
      return { gear: 'R', rpm: electricMotorRpm(speed, spec.tireOuterRadius) };
    }
    const reverseRatio = clamp01(Math.abs(speed) / MAX_REVERSE_SPEED);
    return { gear: 'R', rpm: Math.round(900 + reverseRatio * 4200) };
  }
  if (speed < REST_SPEED) {
    return { gear: 'N', rpm: electric ? 0 : 800 };
  }
  if (electric) {
    return { gear: 'D', rpm: electricMotorRpm(speed, spec.tireOuterRadius) };
  }

  const speedRatio = clamp01(speed / Math.max(REST_SPEED, spec.maxForwardSpeed));
  const boundaries = [0, 0.12, 0.26, 0.42, 0.6, 0.8, 1.01];
  let gearIndex = 1;
  while (gearIndex < 6 && speedRatio >= boundaries[gearIndex]) {
    gearIndex += 1;
  }
  const lower = boundaries[gearIndex - 1];
  const upper = boundaries[gearIndex];
  const withinGear = clamp01((speedRatio - lower) / Math.max(0.01, upper - lower));
  return {
    gear: gearIndex as 1 | 2 | 3 | 4 | 5 | 6,
    rpm: Math.round(950 + withinGear * 6050),
  };
}

interface SuspensionStep {
  wheelTravel: WheelTravel;
  wheelVelocity: WheelTravel;
  bodyHeave: number;
  bodyHeaveVelocity: number;
  roadImpact: number;
  /** 요철이 만든 추가 롤·피치(rad). 가감속 기울기에 더합니다. */
  bumpRoll: number;
  bumpPitch: number;
}

/**
 * 바퀴 네 곳의 접지 높이를 각각 샘플링해 스트로크를 적분합니다.
 *
 * 기준면은 "평지"가 아니라 **차량 중심을 지나는 지형 접평면**입니다. 언덕의 긴 경사는
 * 이미 CarDriver의 지형 자세(applyTerrainPose)가 처리하므로, 그것을 여기서 다시 세면
 * 오르막에서 스트로크가 최대치에 붙어 서스펜션이 굳어 버립니다. 접평면을 빼면 남는 것은
 * 짧은 파장 요철뿐이고, 그것이 서스펜션이 실제로 반응해야 하는 성분입니다.
 *
 * 적분은 속도 상태를 가진 스프링-댐퍼입니다. 위치만 감쇠하면 감쇠비를 조절할 수 없어
 * 컴포트/랠리 셋업이 구분되지 않습니다.
 */
function stepSuspension(
  state: VehicleState,
  spec: VehicleSpec,
  dt: number,
  courseRoute?: CourseRoute
): SuspensionStep {
  // 아래 detailScale 계산에 쓰는 표본 간격입니다.
  const cos = Math.cos(state.heading);
  const sin = Math.sin(state.heading);
  const center = sampleDrivingTerrain(state.x, state.z);
  const sampleSpacing = Math.abs(state.speed) * dt;
  const travel = createWheelTravel();
  const velocity = createWheelTravel();
  let impactVelocity = 0;
  let sum = 0;

  const offsets: Array<[WheelCorner, number, number]> = [
    ['frontLeft', spec.frontWheelX, -spec.wheelHalfTrack],
    ['frontRight', spec.frontWheelX, spec.wheelHalfTrack],
    ['rearLeft', spec.rearWheelX, -spec.wheelHalfTrack],
    ['rearRight', spec.rearWheelX, spec.wheelHalfTrack],
  ];

  for (const [corner, localX, localZ] of offsets) {
    const worldX = state.x + localX * cos + localZ * sin;
    const worldZ = state.z - localX * sin + localZ * cos;
    const offsetX = worldX - state.x;
    const offsetZ = worldZ - state.z;

    const contact = sampleDrivingTerrain(worldX, worldZ);
    const surfaceType = sampleDrivingSurfaceType(worldX, worldZ, courseRoute);
    const detailScale = resolveDetailScale(sampleSpacing, surfaceType);
    /*
     * 서브스텝은 시간 고정이라 속도가 오르면 샘플 간 거리가 늘어납니다(322km/h에서 0.75m).
     * 짧은 옥타브가 표본화 한계 아래로 내려가면 요철이 잡음이 되므로 속도에 따라 줄입니다.
     */
    const bump = sampleRoadProfile(worldX, worldZ, surfaceType, detailScale);
    // 차량 중심의 접평면 높이. 긴 경사는 여기에 모두 들어갑니다.
    const planeHeight = center.height + center.slopeX * offsetX + center.slopeZ * offsetZ;
    /*
     * bumpResponse는 타이어가 요철을 차체로 전달하는 비율입니다. 스프링 상수에 곱하면
     * 고유진동수까지 바뀌어 셋업의 의미가 흐려지므로 입력 쪽에만 적용합니다.
     */
    const target = clamp(
      (contact.height + bump - planeHeight) * spec.bumpResponse,
      spec.suspensionTravel
    );

    const previousTravel = state.wheelTravel[corner];
    const previousVelocity = state.wheelVelocity[corner];
    const acceleration =
      (target - previousTravel) * spec.suspensionStiffness -
      previousVelocity * spec.suspensionDamping;
    const nextVelocity = previousVelocity + acceleration * dt;
    const nextTravel = clamp(previousTravel + nextVelocity * dt, spec.suspensionTravel);

    travel[corner] = nextTravel;
    // 스트로크가 한계에 닿으면 속도를 죽입니다(범프 스톱). 아니면 값이 진동합니다.
    velocity[corner] = Math.abs(nextTravel) >= spec.suspensionTravel ? 0 : nextVelocity;
    sum += nextTravel;
    impactVelocity = Math.max(impactVelocity, Math.abs(nextVelocity));
  }

  const bodyHeave = sum / 4;
  const bodyHeaveVelocity = (bodyHeave - state.bodyHeave) / Math.max(dt, 1e-4);
  const halfTrack = Math.max(0.4, spec.wheelHalfTrack);
  const halfBase = Math.max(0.6, spec.wheelbase / 2);
  const rightAverage = (travel.frontRight + travel.rearRight) / 2;
  const leftAverage = (travel.frontLeft + travel.rearLeft) / 2;
  const frontAverage = (travel.frontLeft + travel.frontRight) / 2;
  const rearAverage = (travel.rearLeft + travel.rearRight) / 2;

  return {
    wheelTravel: travel,
    wheelVelocity: velocity,
    bodyHeave,
    bodyHeaveVelocity,
    // 스트로크 속도를 체감값으로 정규화합니다(0.6m/s면 확실한 충격).
    roadImpact: clamp01(impactVelocity / 0.6),
    bumpRoll: Math.atan2(rightAverage - leftAverage, halfTrack * 2),
    bumpPitch: Math.atan2(frontAverage - rearAverage, halfBase * 2),
  };
}

function integrateVehicle(
  state: VehicleState,
  input: VehicleInput,
  dt: number,
  spec: VehicleSpec,
  globalSurface: VehicleSurfaceCondition,
  spawn: VehicleSpawn,
  assists: DrivingAssists,
  regenMode: RegenMode,
  courseRoute?: CourseRoute
): VehicleState {
  const terrain = sampleDrivingTerrain(state.x, state.z);
  const resolvedSurface = resolveDrivingSurface(
    state.x,
    state.z,
    globalSurface,
    courseRoute
  );
  // 노면 × 날씨에 타이어 튜닝 계수를 한 번 더 곱합니다.
  const surface: VehicleSurfaceCondition = {
    gripMultiplier: resolvedSurface.effectiveGrip * spec.tireGripMultiplier,
    rollingResistanceMultiplier:
      resolvedSurface.effectiveRollingResistance * spec.tireRollingResistanceMultiplier,
    roadWetness: resolvedSurface.wetness,
  };
  const forwardX = Math.cos(state.heading);
  const forwardZ = -Math.sin(state.heading);
  const forwardGrade = terrain.slopeX * forwardX + terrain.slopeZ * forwardZ;

  // 경사 접선 방향의 중력 성분. 양의 grade(오르막)는 전진 속도를 줄입니다.
  const gradeAcceleration = (-GRAVITY * forwardGrade) / Math.sqrt(1 + forwardGrade * forwardGrade);
  const normalScale = 1 / Math.sqrt(
    1 + terrain.slopeX * terrain.slopeX + terrain.slopeZ * terrain.slopeZ
  );
  const surfaceGrip = Math.max(0.2, surface.gripMultiplier);
  const lateralGrip = DRY_TIRE_GRIP_ACCEL * surfaceGrip * normalScale;

  const steerAngle = stepSteerAngle(state.steerAngle, input, state.speed, dt, spec);
  const desiredYawRate =
    spec.wheelbase <= 0 ? 0 : (state.speed * Math.tan(steerAngle)) / spec.wheelbase;
  const requestedLateralAcceleration = state.speed * desiredYawRate;
  const requestedLongitudinalAcceleration = requestedTireAcceleration(
    state.speed,
    input,
    spec,
    surface,
    regenMode
  );

  // 고성능 AWD 차량의 제조사 0→100 가속은 1g를 넘을 수 있습니다. 그 차이는 종방향
  // 접지 한계에만 반영하고 횡그립은 공통 타이어 한계를 유지합니다.
  const calibrationSpeed = Math.min(
    HUNDRED_KMH_IN_METRES_PER_SECOND,
    spec.maxForwardSpeed * 0.92
  );
  const launchForce =
    spec.forwardAcceleration +
    rollingResistanceFor(surface) +
    aerodynamicDragMagnitude(calibrationSpeed, spec);
  const longitudinalGrip =
    Math.max(DRY_TIRE_GRIP_ACCEL, launchForce * 1.025) * surfaceGrip * normalScale;

  // 주행 보조가 켜진 종방향 요청은 해당 축에 우선 배분하고, 남은 마찰 타원만
  // 횡방향에 사용합니다. 꺼져 있으면 두 요청을 함께 포화시켜 잠김/휠스핀을 허용합니다.
  const combinedDemand = Math.hypot(
    requestedLateralAcceleration / Math.max(REST_SPEED, lateralGrip),
    requestedLongitudinalAcceleration / Math.max(REST_SPEED, longitudinalGrip)
  );
  let supportedLateralAcceleration: number;
  let supportedLongitudinalAcceleration: number;
  let absActive = false;
  let tcsActive = false;
  const braking = isBraking(input) && requestedLongitudinalAcceleration < 0;
  const accelerating = input.throttle > 0 && requestedLongitudinalAcceleration > 0;

  if (braking && assists.abs) {
    const absLimit = longitudinalGrip * spec.absLimitRatio;
    supportedLongitudinalAcceleration = clamp(requestedLongitudinalAcceleration, absLimit);
    const longitudinalUse = Math.abs(supportedLongitudinalAcceleration) / longitudinalGrip;
    const remainingLateralGrip = lateralGrip * Math.sqrt(Math.max(0, 1 - longitudinalUse ** 2));
    supportedLateralAcceleration = clamp(requestedLateralAcceleration, remainingLateralGrip);
    absActive =
      Math.abs(requestedLongitudinalAcceleration) > absLimit || combinedDemand > 1;
  } else if (accelerating && assists.tcs) {
    const tcsLimit = longitudinalGrip * 0.9;
    supportedLongitudinalAcceleration = clamp(requestedLongitudinalAcceleration, tcsLimit);
    const longitudinalUse = Math.abs(supportedLongitudinalAcceleration) / longitudinalGrip;
    const remainingLateralGrip = lateralGrip * Math.sqrt(Math.max(0, 1 - longitudinalUse ** 2));
    supportedLateralAcceleration = clamp(requestedLateralAcceleration, remainingLateralGrip);
    tcsActive =
      Math.abs(requestedLongitudinalAcceleration) > tcsLimit || combinedDemand > 1;
  } else {
    const gripScale = combinedDemand > 1 ? 1 / combinedDemand : 1;
    supportedLateralAcceleration = requestedLateralAcceleration * gripScale;
    supportedLongitudinalAcceleration = requestedLongitudinalAcceleration * gripScale;
  }

  if (braking && !assists.abs) {
    const lockSeverity = clamp01(
      (Math.abs(requestedLongitudinalAcceleration) / Math.max(REST_SPEED, longitudinalGrip) - 0.72) /
        0.55
    );
    // 잠긴 타이어는 횡력을 거의 만들지 못합니다. ABS를 끈 상태의 방향 안정성 손실입니다.
    supportedLateralAcceleration *= 1 - lockSeverity * 0.72;
  }

  const tractionLimit = limitElectricTraction(
    state,
    spec,
    supportedLongitudinalAcceleration,
    input,
    dt
  );
  supportedLongitudinalAcceleration = tractionLimit.acceleration;

  const targetYawRate =
    Math.abs(state.speed) < REST_SPEED ? 0 : supportedLateralAcceleration / state.speed;
  let yawRate = approach(state.yawRate, targetYawRate, YAW_RATE_RESPONSE, dt);

  // 타이어가 만들지 못한 횡가속도를 차체 오른쪽 방향 속도로 축적합니다. 조향을 풀면
  // 타이어의 셀프 얼라이닝/횡마찰을 근사한 감쇠가 다시 0으로 회복시킵니다.
  const unsupportedLateralAcceleration =
    requestedLateralAcceleration - supportedLateralAcceleration;
  const lateralAcceleration = clamp(
    unsupportedLateralAcceleration * SLIP_LATERAL_ACCEL_SCALE -
      state.lateralSpeed * SLIP_RECOVERY_RATE * Math.max(0.35, surface.gripMultiplier),
    lateralGrip
  );
  const maxLateralSpeed = Math.max(1.2, Math.abs(state.speed) * MAX_LATERAL_SPEED_RATIO);
  let lateralSpeed = clamp(state.lateralSpeed + lateralAcceleration * dt, maxLateralSpeed);
  const provisionalSlipAngle = Math.atan2(lateralSpeed, Math.max(1, Math.abs(state.speed)));
  const escActive =
    assists.esc &&
    Math.abs(state.speed) > 8 &&
    (Math.abs(provisionalSlipAngle) > 0.012 || Math.abs(yawRate - targetYawRate) > 0.1);
  if (escActive) {
    lateralSpeed = approach(lateralSpeed, 0, 8.5, dt);
    yawRate = approach(yawRate, targetYawRate * 0.88, 5.2, dt);
  }

  const longitudinalSlipReference =
    absActive || tcsActive || tractionLimit.limited
      ? supportedLongitudinalAcceleration
      : requestedLongitudinalAcceleration;
  const longitudinal = stepLongitudinalSpeed(
    state.speed,
    input,
    dt,
    spec,
    gradeAcceleration,
    longitudinalSlipReference,
    supportedLongitudinalAcceleration,
    longitudinalGrip,
    surface,
    regenMode
  );

  const averageSpeed = (state.speed + longitudinal.speed) * 0.5;
  const averageLateralSpeed = (state.lateralSpeed + lateralSpeed) * 0.5;
  const midpointHeading = state.heading + yawRate * dt * 0.5;
  const midpointForwardX = Math.cos(midpointHeading);
  const midpointForwardZ = -Math.sin(midpointHeading);
  const midpointRightX = Math.sin(midpointHeading);
  const midpointRightZ = Math.cos(midpointHeading);
  const midpointForwardGrade =
    terrain.slopeX * midpointForwardX + terrain.slopeZ * midpointForwardZ;
  const midpointRightGrade = terrain.slopeX * midpointRightX + terrain.slopeZ * midpointRightZ;

  // speed는 노면을 따라 잰 값이므로 x/z 이동에는 경사로 늘어난 실제 이동 거리의 수평
  // 성분만 사용합니다. 바퀴 회전과 계기판은 노면 기준 속도를 그대로 사용합니다.
  const horizontalForwardSpeed = averageSpeed / Math.sqrt(1 + midpointForwardGrade ** 2);
  const horizontalLateralSpeed = averageLateralSpeed / Math.sqrt(1 + midpointRightGrade ** 2);
  const proposedX =
    state.x +
    (midpointForwardX * horizontalForwardSpeed + midpointRightX * horizontalLateralSpeed) * dt;
  const proposedZ =
    state.z +
    (midpointForwardZ * horizontalForwardSpeed + midpointRightZ * horizontalLateralSpeed) * dt;
  const heading = wrapAngle(state.heading + yawRate * dt);

  /*
   * 차축을 잇는 캡슐을 이동 구간 전체에 쓸어 작은 콘도 고속에서 관통하지 않게 합니다.
   * wheelHalfTrack에 타이어 반경 일부를 더하면 차종별 차폭을 보수적으로 근사하면서
   * 사이드미러처럼 실제 충돌에서 제외할 돌출부는 포함하지 않습니다.
   */
  const collisionFootprint = {
    frontX: spec.frontWheelX,
    rearX: spec.rearWheelX,
    halfWidth: spec.wheelHalfTrack + spec.tireOuterRadius * 0.22,
  };
  const collisionResult = resolveCourseObstacleCollision(
    {
      fromX: state.x,
      fromZ: state.z,
      toX: proposedX,
      toZ: proposedZ,
      fromHeading: state.heading,
      toHeading: heading,
      speed: longitudinal.speed,
      lateralSpeed,
      yawRate,
    },
    collisionFootprint,
    courseRoute?.obstacles
  );
  const x = collisionResult.x;
  const z = collisionResult.z;
  const speed = collisionResult.speed;
  lateralSpeed = collisionResult.lateralSpeed;
  yawRate = collisionResult.yawRate;

  if (Math.hypot(x, z) > DRIVING_RESPAWN_RADIUS) {
    return respawnVehicle(state, SPAWN_REASONS.RUNOFF, spawn);
  }

  const priorContactActive = isCourseObstacleContactActive(
    state.x,
    state.z,
    state.heading,
    collisionFootprint,
    courseRoute?.obstacles,
    state.collision.obstacleId
  );
  const emitsCollision =
    collisionResult.impact > 0
    && collisionResult.obstacleId !== null
    && (
      collisionResult.obstacleId !== state.collision.obstacleId
      || !priorContactActive
    );
  const previousContactRemains = isCourseObstacleContactActive(
    x,
    z,
    heading,
    collisionFootprint,
    courseRoute?.obstacles,
    state.collision.obstacleId
  );
  const activeObstacleId =
    collisionResult.obstacleId
    ?? (previousContactRemains ? state.collision.obstacleId : null);
  const collision: VehicleCollisionState = {
    impact: clamp01(Math.max(collisionResult.impact, state.collision.impact - dt * 2.8)),
    sequence: state.collision.sequence + (emitsCollision ? 1 : 0),
    strength: emitsCollision ? collisionResult.impact : state.collision.strength,
    normalX: emitsCollision ? collisionResult.normalX : state.collision.normalX,
    normalZ: emitsCollision ? collisionResult.normalZ : state.collision.normalZ,
    obstacleId: activeObstacleId,
  };
  // 기존 구동 가속도는 유지하고 실제 충돌 impulse만 별도로 더합니다.
  const actualLongitudinalAcceleration =
    longitudinal.acceleration
    + (speed - longitudinal.speed) / Math.max(dt, 1e-4);

  const slipAngle = Math.atan2(lateralSpeed, Math.max(1, Math.abs(speed)));
  const lateralDemandRatio =
    lateralGrip <= REST_SPEED ? 0 : Math.abs(requestedLateralAcceleration) / lateralGrip;
  const forceSlip = clamp01((lateralDemandRatio - 0.78) / 0.72);
  const angleSlip = clamp01((Math.abs(slipAngle) - 0.018) / 0.14);
  const speedGate = clamp01(
    (Math.abs(speed) - SLIP_MIN_SPEED) / (SLIP_FULL_SPEED - SLIP_MIN_SPEED)
  );
  const rawSlip =
    clamp01(Math.max(forceSlip, angleSlip, Math.abs(longitudinal.longitudinalSlip))) * speedGate;
  const slipIntensity = approach(
    state.slipIntensity,
    rawSlip,
    rawSlip > state.slipIntensity ? SLIP_RISE_RATE : SLIP_FALL_RATE,
    dt
  );

  const rollGoal =
    clamp(supportedLateralAcceleration / LATERAL_ACCEL_FOR_MAX_ROLL, 1) * MAX_BODY_TILT;
  const pitchGoal =
    clamp(actualLongitudinalAcceleration / LONGITUDINAL_ACCEL_FOR_MAX_PITCH, 1) * MAX_BODY_TILT;

  // 충돌로 잃은 속도까지 반영해 바퀴가 오브젝트 앞에서 계속 고속 회전하지 않게 합니다.
  const resolvedAverageSpeed = (state.speed + speed) * 0.5;
  const resolvedAverageLateralSpeed = (state.lateralSpeed + lateralSpeed) * 0.5;
  const wheelSurfaceSpeed =
    resolvedAverageSpeed +
    Math.abs(resolvedAverageSpeed) * clamp(longitudinal.longitudinalSlip, 1) * 0.18;
  const spinDelta =
    spec.tireOuterRadius <= 0 ? 0 : (wheelSurfaceSpeed * dt) / spec.tireOuterRadius;
  const wheelSpin = (state.wheelSpin - spinDelta) % (Math.PI * 2);
  const powertrain = deriveGearAndRpm(speed, spec);
  /*
   * 바퀴 출력 = 질량 × 접지된 구동가속도 × 속도.
   *
   * 요청값이 아니라 접지된 값을 쓰는 이유: 휠스핀이 나면 넘친 요청은 노면에 전달되지
   * 않습니다. 후진은 가속도와 속도가 함께 음수여서 곱이 양수가 되고(모터가 일을 함),
   * 제동은 부호가 갈려 음수 — 회생으로 읽힙니다. 충돌 손실은 모터 출력으로 환산하지
   * 않으므로 여기에는 충돌 전 구동계 평균 속도를 유지합니다.
   */
  const wheelPowerKw = (spec.massKg * supportedLongitudinalAcceleration * averageSpeed) / 1000;
  const powerKw =
    spec.powertrain === 'ev' && wheelPowerKw < 0
      ? Math.max(-spec.regenLimitKw, wheelPowerKw)
      : wheelPowerKw;
  const energy = stepElectricEnergy(
    state.energy,
    spec.electricEnergy,
    wheelPowerKw,
    resolvedAverageSpeed,
    resolvedAverageLateralSpeed,
    dt
  );

  /*
   * 서스펜션은 이동이 끝난 새 위치에서 계산합니다. 이전 위치로 계산하면 요철 반응이
   * 한 스텝 늦어 고속에서 차체가 지나간 뒤에 흔들립니다.
   */
  const movedState: VehicleState = {
    ...state,
    x,
    z,
    heading,
    speed,
    lateralSpeed,
    yawRate,
    collision,
  };
  const suspension = stepSuspension(movedState, spec, dt, courseRoute);
  const roadImpact = Math.max(
    suspension.roadImpact,
    // 충격은 즉시 올라가고 서서히 가라앉습니다(진동/HUD가 읽기 좋게).
    state.roadImpact - dt * 3.2
  );

  return {
    x,
    z,
    heading,
    speed,
    gear: powertrain.gear,
    rpm: powertrain.rpm,
    powerKw,
    energy,
    gradePercent: forwardGrade * 100,
    longitudinalAcceleration: actualLongitudinalAcceleration,
    surfaceType: resolvedSurface.type,
    surfaceWetness: resolvedSurface.wetness,
    effectiveGrip: surface.gripMultiplier,
    absActive,
    tcsActive,
    escActive,
    lateralSpeed,
    yawRate,
    steerAngle,
    slipAngle,
    longitudinalSlip: longitudinal.longitudinalSlip,
    instantaneousSlip: rawSlip,
    slipIntensity,
    roll: approach(state.roll, rollGoal, MAX_BODY_TILT * BODY_TILT_RATE, dt),
    pitch: approach(state.pitch, pitchGoal, MAX_BODY_TILT * BODY_TILT_RATE, dt),
    suspensionRoll: suspension.bumpRoll,
    suspensionPitch: suspension.bumpPitch,
    wheelTravel: suspension.wheelTravel,
    wheelVelocity: suspension.wheelVelocity,
    bodyHeave: suspension.bodyHeave,
    bodyHeaveVelocity: suspension.bodyHeaveVelocity,
    roadImpact: clamp01(roadImpact),
    collision,
    wheelSpin,
    spawnCount: state.spawnCount,
    spawnReason: state.spawnReason,
  };
}

/**
 * 한 렌더 프레임 진행.
 *
 * 긴 프레임은 잘라내고 1/120초 고정 서브스텝으로 나눕니다. 경사 전환과 타이어 포화는
 * 프레임률에 민감하므로 30fps와 120fps에서 다른 차가 되지 않게 하기 위한 처리입니다.
 */
export function stepVehicle(
  state: VehicleState,
  input: VehicleInput,
  dt: number,
  spec: VehicleSpec,
  surface: VehicleSurfaceCondition = DEFAULT_VEHICLE_SURFACE,
  spawn: VehicleSpawn = DEFAULT_VEHICLE_SPAWN,
  assists: DrivingAssists = DEFAULT_DRIVING_ASSISTS,
  regenMode: RegenMode = 'standard',
  courseRoute?: CourseRoute
): VehicleState {
  let remaining = Math.min(Math.max(dt, 0), MAX_FRAME_STEP_SECONDS);
  if (remaining === 0) {
    return state;
  }

  let current = state;
  while (remaining > 1e-9) {
    const step = Math.min(PHYSICS_STEP_SECONDS, remaining);
    const previousSpawnCount = current.spawnCount;
    current = integrateVehicle(
      current,
      input,
      step,
      spec,
      surface,
      spawn,
      assists,
      regenMode,
      courseRoute
    );
    if (current.spawnCount !== previousSpawnCount) {
      return current;
    }
    remaining -= step;
  }
  return current;
}

/* ---------------------------------------------------------------- 표시 */

/** 차량 로컬 좌표를 앱 좌표로 변환합니다. */
export function localToWorld(
  state: VehicleState,
  localX: number,
  localZ: number
): { x: number; z: number } {
  const cos = Math.cos(state.heading);
  const sin = Math.sin(state.heading);
  return {
    x: state.x + localX * cos + localZ * sin,
    z: state.z - localX * sin + localZ * cos,
  };
}

function roundDrivingKmh(state: VehicleState): number {
  return Math.round(Math.abs(state.speed) * 3.6);
}

export function isDrivingSpeedIdle(state: VehicleState): boolean {
  return roundDrivingKmh(state) === 0;
}

export function formatDrivingSpeed(state: VehicleState): string {
  const kmh = roundDrivingKmh(state);
  if (kmh === 0) {
    return '정지';
  }
  return state.speed < 0 ? `후진 ${kmh} km/h` : `${kmh} km/h`;
}
